import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/features/auth/context/authProvider'
import EmployeesAPI from '@/features/employee-management/api/EmployeesAPI'
import ImageUploadButton from '@/shared/components/ImageUploadButton/ImageUploadButton'
import { useSnackbar } from '@/shared/components/Snackbar/SnackbarProvider'
import IMAGE_SLOTS_KEYS from '@/shared/store/imageSlots.model'
import { useImageSlots } from '@/shared/store/useImageSlots'
import { useWriteProfile } from '../hooks/useWriteProfile'

const AVATAR_SLOT = IMAGE_SLOTS_KEYS.ACCOUNT_AVATAR

/**
 * The owner's photo, saved as soon as it is cropped or deleted, as the
 * restaurant's logo is: an upload of its own, outside the section's Save. It
 * goes through the avatar endpoint the employee form uses first, because
 * Firebase only stores the address.
 */
function AccountPhotoField() {
  const { t } = useTranslation()
  const { currentUser } = useAuth()
  const { showSnackbar } = useSnackbar()
  const writeProfile = useWriteProfile()
  const [isRemoved, setIsRemoved] = useState(false)

  const slot = useImageSlots(s => s.slots[AVATAR_SLOT])
  const clearSlot = useImageSlots(s => s.clear)
  const croppedPhoto = slot?.croppedBlob ?? null

  // A crop left behind would come back as a draft the next time this opens.
  useEffect(() => () => clearSlot(AVATAR_SLOT), [clearSlot])

  const reportFailure = () =>
    showSnackbar(
      t('ALERTS.SETTINGS_SAVE_ERROR.TITLE'),
      t('ALERTS.SETTINGS_SAVE_ERROR.DESCRIPTION'),
      'error'
    )

  useEffect(() => {
    if (!croppedPhoto) return
    const upload = async () => {
      try {
        const file = new File([croppedPhoto], 'avatar', {
          type: slot?.mime || 'image/png'
        })
        const uploaded = await EmployeesAPI.uploadAvatar(file)
        // The same form DynamicForm reads a stored image back in.
        await writeProfile({
          photoURL: `${uploaded.imageUrl}?t=${uploaded.version}&alt=media`
        })
      } catch {
        reportFailure()
      } finally {
        // Saved or not, the crop is done with: the saved photo shows again.
        clearSlot(AVATAR_SLOT)
      }
    }
    upload()
    // Keyed on the crop alone: a new one is the only thing that uploads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [croppedPhoto])

  const remove = () => {
    // Hidden at once; the saved address only clears once Firebase has.
    setIsRemoved(true)
    writeProfile({ photoURL: null })
      .catch(reportFailure)
      .finally(() => setIsRemoved(false))
  }

  return (
    <ImageUploadButton
      imageShape="circle"
      label={t('SETTINGS.ACCOUNT.PHOTO')}
      slotId={AVATAR_SLOT}
      imageUrl={isRemoved ? undefined : (currentUser?.photoURL ?? undefined)}
      // The logo's sizes: the dialog takes the image's, and at 200 the drop
      // zone's copy spilled out over its title.
      imageSize={{ width: 440, height: 440 }}
      stencilSize={{ width: 264, height: 264 }}
      onDelete={remove}
    />
  )
}

export default AccountPhotoField
